import type { HTMLAttributes, ElementType } from 'react';

interface LabelCapsProps extends HTMLAttributes<HTMLElement> {
  /** Element to render as. Default: `div`. Use `span` inline. */
  as?: ElementType;
}

/** Default text color emitted when the caller does not supply their own. */
const DEFAULT_TEXT_COLOR = 'text-indigo-600/70';

/**
 * True when `className` already sets a text COLOR (named scale, keyword, or
 * arbitrary color value) — as opposed to a text SIZE (`text-sm`, `text-[10px]`)
 * or alignment (`text-center`). Used so a caller override actually wins instead
 * of fighting the built-in default: emitting both `text-indigo-600/70` and the
 * override leaves the winner up to Tailwind's generated source order, which
 * silently dropped overrides like `text-emerald-300/80` on dark surfaces and
 * produced dark-on-dark text (WCAG color-contrast failure rm-0101).
 */
export function hasTextColor(className: string): boolean {
  return /(?:^|\s)text-(?:white|black|transparent|current|inherit|[a-z]+-\d{1,3})(?:\/\d{1,3})?(?=\s|$)/.test(
    className,
  ) || /(?:^|\s)text-\[(?:#|rgb|hsl|var|color)/.test(className);
}

/**
 * Small uppercase eyebrow text — the EstateIntelligence design system's
 * "label-caps" typography style (12px, 700 weight, 0.1em tracking).
 *
 * Use for: section eyebrows ("Q2 2026 Portfolio"), table headers,
 * stat-card category labels, anything that frames a primary headline.
 *
 * Color defaults to muted forest (`text-indigo-600/70`) so the headline
 * directly below stands out. 70% opacity yields ~5.4:1 contrast on
 * parchment/white — meets WCAG 2 AA 4.5:1 for normal text. Override
 * with className if you need a different tone (e.g. on dark surfaces
 * use `text-emerald-300/80`); the override fully replaces the default
 * so it can't be lost to Tailwind class-ordering.
 */
export function LabelCaps({
  as: Tag = 'div',
  className = '',
  children,
  ...rest
}: LabelCapsProps) {
  // Only emit the default color when the caller hasn't supplied one of their
  // own — otherwise both land in the class list and the winner is decided by
  // Tailwind's source order, not the caller's intent.
  const color = hasTextColor(className) ? '' : DEFAULT_TEXT_COLOR;
  const classes = ['label-caps', color, className].filter(Boolean).join(' ');
  return (
    <Tag className={classes} {...rest}>
      {children}
    </Tag>
  );
}
